import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import PlayerProfileModal from "../components/PlayerProfileModal";
import {
  listLeagues,
  listOrganizations,
  listPlayerParents,
  listPitchLogs,
  listPlayers,
  listRestProfiles,
  listRestRules,
  listRosterMemberships,
  listTeamPlayers,
  listTeams,
  listUserAssignments
} from "../graphql/queries";
import { evaluatePitcherAvailability } from "../lib/rulesEngine";
import { buildCoachTeamIds, buildPlayerTeamIds, type RosterMembershipLike, type UserAssignmentLike } from "../lib/relationshipScope";
import type { ActiveRoleScope } from "../lib/roleContext";

interface ViolationRuleRow {
  id: string;
  type: string;
  ruleName: string;
  source: string;
  details: string;
}

interface GameViolationRow {
  id: string;
  playerId: string;
  playerName: string;
  teamId?: string | null;
  teamName: string;
  date: string;
  rules: ViolationRuleRow[];
}

interface Player {
  id: string;
  name: string;
  teamId: string;
}

interface Team {
  id: string;
  name: string;
  organizationId?: string | null;
  leagueId?: string | null;
  coachUsernames?: string[] | null;
}

interface TeamPlayer {
  id: string;
  teamID: string;
  playerID: string;
}

interface PlayerParentLink {
  id: string;
  playerId: string;
  parentEmail: string;
}

interface RosterMembership extends RosterMembershipLike {}

interface UserAssignment extends UserAssignmentLike {}

interface ViolationsPageProps {
  userRoles: string[];
  activeRoleScope?: ActiveRoleScope | null;
}

const ViolationsPage: React.FC<ViolationsPageProps> = ({ userRoles, activeRoleScope }) => {
  const [violations, setViolations] = useState<GameViolationRow[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamPlayers, setTeamPlayers] = useState<TeamPlayer[]>([]);
  const [rosterMemberships, setRosterMemberships] = useState<RosterMembership[]>([]);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([]);
  const [username, setUsername] = useState("");
  const [sortKey, setSortKey] = useState<"date" | "player" | "count">("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [filterText, setFilterText] = useState("");
  const [filterTeam, setFilterTeam] = useState("");
  const [filterMinRules, setFilterMinRules] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [activePlayerModalId, setActivePlayerModalId] = useState<string | null>(null);

  const isCoachOnly =
    userRoles.includes("Coach") &&
    !userRoles.includes("Commish") &&
    !userRoles.includes("SuperAdmin");
  const hasParentRole = userRoles.includes("Parent") || userRoles.includes("Parents");
  const isPrivileged = userRoles.includes("Commish") || userRoles.includes("SuperAdmin");

  const visiblePlayerIds = useMemo(() => {
    return new Set(players.map((player) => player.id));
  }, [players]);

  const sortedViolations = useMemo(() => {
    const normalizedText = filterText.trim().toLowerCase();
    const minRules = Number(filterMinRules);
    return [...violations]
      .filter((violation) => visiblePlayerIds.has(violation.playerId))
      .filter((violation) => {
        if (normalizedText) {
          const haystack = `${violation.playerName} ${violation.teamName} ${violation.date}`.toLowerCase();
          if (!haystack.includes(normalizedText)) {
            return false;
          }
        }
        if (filterTeam && violation.teamName !== filterTeam) {
          return false;
        }
        if (Number.isFinite(minRules) && minRules > 0 && violation.rules.length < minRules) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        let compare = 0;
        if (sortKey === "count") {
          compare = a.rules.length - b.rules.length;
        } else if (sortKey === "player") {
          compare = a.playerName.localeCompare(b.playerName);
        } else {
          compare = a.date.localeCompare(b.date);
        }
        return sortDirection === "asc" ? compare : -compare;
      });
  }, [filterMinRules, filterTeam, filterText, sortDirection, sortKey, violations, visiblePlayerIds]);

  const toggleSort = (key: "date" | "player" | "count") => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  const teamOptions = useMemo(() => {
    const set = new Set<string>();
    for (const violation of violations) {
      if (violation.teamName) {
        set.add(violation.teamName);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [violations]);

  const loadData = async () => {
    try {
      setErrorMessage("");
      const [authUser, playerRes, teamRes, teamPlayerRes, rosterMembershipRes, userAssignmentsRes, orgRes, leagueRes, logsRes, rulesRes, profilesRes, playerParentRes] = await Promise.all([
        Auth.currentAuthenticatedUser(),
        API.graphql(graphqlOperation(listPlayers, { limit: 3000 })),
        API.graphql(graphqlOperation(listTeams, { limit: 1000 })),
        API.graphql(graphqlOperation(listTeamPlayers, { limit: 4000 })),
        API.graphql(graphqlOperation(listRosterMemberships, { limit: 5000 })),
        API.graphql(graphqlOperation(listUserAssignments, { limit: 5000 })),
        API.graphql(graphqlOperation(listOrganizations, { limit: 1000 })),
        API.graphql(graphqlOperation(listLeagues, { limit: 1000 })),
        API.graphql(graphqlOperation(listPitchLogs, { limit: 6000 })),
        API.graphql(graphqlOperation(listRestRules, { limit: 1000 })),
        API.graphql(graphqlOperation(listRestProfiles, { limit: 3000 })),
        API.graphql(graphqlOperation(listPlayerParents, { limit: 6000 }))
      ]);

      const email = (authUser?.attributes?.email ?? "").toLowerCase();
      const loadedPlayers = ((playerRes as any)?.data?.listPlayers?.items ?? []).filter(Boolean);
      const loadedTeams = ((teamRes as any)?.data?.listTeams?.items ?? []).filter(Boolean);
      const loadedTeamPlayers = ((teamPlayerRes as any)?.data?.listTeamPlayers?.items ?? []).filter(Boolean);
      const loadedRosterMemberships = ((rosterMembershipRes as any)?.data?.listRosterMemberships?.items ?? []).filter(Boolean);
      const loadedUserAssignments = ((userAssignmentsRes as any)?.data?.listUserAssignments?.items ?? []).filter(Boolean);
      const loadedOrganizations = ((orgRes as any)?.data?.listOrganizations?.items ?? []).filter(Boolean);
      const loadedLeagues = ((leagueRes as any)?.data?.listLeagues?.items ?? []).filter(Boolean);
      const loadedLogs = ((logsRes as any)?.data?.listPitchLogs?.items ?? []).filter(Boolean);
      const loadedRules = ((rulesRes as any)?.data?.listRestRules?.items ?? []).filter(Boolean);
      const loadedProfiles = ((profilesRes as any)?.data?.listRestProfiles?.items ?? []).filter(Boolean);
      const loadedPlayerParents: PlayerParentLink[] = ((playerParentRes as any)?.data?.listPlayerParents?.items ?? []).filter(Boolean);
      const localCoachTeamIds = isCoachOnly
        ? buildCoachTeamIds({ username: authUser?.username ?? "", teams: loadedTeams, userAssignments: loadedUserAssignments })
        : new Set<string>();

      const parentPlayerIds = new Set<string>(
        (activeRoleScope?.targetType === "parent" || (hasParentRole && !isPrivileged))
          ? loadedPlayerParents
              .filter((link) => (link.parentEmail ?? "").toLowerCase() === email)
              .map((link) => link.playerId)
          : []
      );

      const byPlayer = buildPlayerTeamIds({ players: loadedPlayers, teamPlayers: loadedTeamPlayers, rosterMemberships: loadedRosterMemberships });

      const roleScopedIds = new Set<string>();
      const activeScopedTeamIds = new Set<string>();
      if (activeRoleScope?.targetType === "team" && activeRoleScope.teamId) {
        activeScopedTeamIds.add(activeRoleScope.teamId);
      }
      if (activeRoleScope?.targetType === "organization" && activeRoleScope.organizationId) {
        loadedTeams
          .filter((team: Team) => team.organizationId === activeRoleScope.organizationId)
          .forEach((team: Team) => activeScopedTeamIds.add(team.id));
      }
      if (activeRoleScope?.targetType === "league" && activeRoleScope.leagueId) {
        loadedTeams
          .filter((team: Team) => team.leagueId === activeRoleScope.leagueId)
          .forEach((team: Team) => activeScopedTeamIds.add(team.id));
      }

      if (activeRoleScope?.targetType === "parent") {
        for (const playerId of parentPlayerIds) {
          roleScopedIds.add(playerId);
        }
      } else if (activeScopedTeamIds.size > 0) {
        for (const player of loadedPlayers) {
          const memberships = byPlayer[player.id] ?? new Set<string>();
          if ([...memberships].some((teamId) => activeScopedTeamIds.has(teamId))) {
            roleScopedIds.add(player.id);
          }
        }
      } else if (isCoachOnly) {
        for (const player of loadedPlayers) {
          const memberships = byPlayer[player.id] ?? new Set<string>();
          if ([...memberships].some((teamId) => localCoachTeamIds.has(teamId))) {
            roleScopedIds.add(player.id);
          }
        }
      }
      if (hasParentRole && !isPrivileged) {
        for (const playerId of parentPlayerIds) {
          roleScopedIds.add(playerId);
        }
      }
      const hasActiveScopedFilter = activeRoleScope?.targetType === "parent" || activeScopedTeamIds.size > 0;
      const visiblePlayers = hasActiveScopedFilter || (hasParentRole && !isPrivileged) || isCoachOnly
        ? loadedPlayers.filter((player: Player) => roleScopedIds.has(player.id))
        : loadedPlayers;
      const visiblePlayerIdSet = new Set<string>(visiblePlayers.map((player: Player) => player.id));
      const visibleLogs = hasActiveScopedFilter || (hasParentRole && !isPrivileged) || isCoachOnly
        ? loadedLogs.filter((log: any) => visiblePlayerIdSet.has(log.playerId))
        : loadedLogs;

      const availability = evaluatePitcherAvailability({
        players: visiblePlayers,
        teams: loadedTeams,
        leagues: loadedLeagues,
        organizations: loadedOrganizations,
        rules: loadedRules,
        profiles: loadedProfiles,
        logs: visibleLogs,
        teamPlayers: loadedTeamPlayers
      });

      const grouped = new Map<string, GameViolationRow>();
      for (const entry of availability) {
        for (const violation of entry.violations) {
          const violationTeamName = violation.teamName ?? entry.teamName;
          const key = `${entry.playerId}|${violation.teamId ?? violationTeamName}|${violation.occurredOn}`;
          const existing = grouped.get(key);
          const rule: ViolationRuleRow = {
            id: `${entry.playerId}-${violation.id}`,
            type: violation.type,
            ruleName: violation.ruleName,
            source: violation.source,
            details: violation.message
          };
          if (existing) {
            existing.rules.push(rule);
            continue;
          }
          grouped.set(key, {
            id: key,
            playerId: entry.playerId,
            playerName: entry.playerName,
            teamId: violation.teamId,
            teamName: violationTeamName,
            date: violation.occurredOn,
            rules: [rule]
          });
        }
      }

      const rows = [...grouped.values()].map((row) => ({
        ...row,
        rules: row.rules.filter(
          (rule, idx, arr) =>
            arr.findIndex((item) => item.type === rule.type && item.ruleName === rule.ruleName && item.source === rule.source) === idx
        )
      }));

      setUsername(authUser?.username ?? "");
      setViolations(rows);
      setPlayers(visiblePlayers);
      setTeams(loadedTeams);
      setTeamPlayers(loadedTeamPlayers);
      setRosterMemberships(loadedRosterMemberships);
      setUserAssignments(loadedUserAssignments);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Failed to load violations.");
    }
  };

  useEffect(() => {
    loadData();
  }, [activeRoleScope?.id]);

  return (
    <>
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-2">Pitch Violations</h1>
      <p className="text-gray-600 mb-6">Calculated from pitch logs and active rulesets.</p>
      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}

      <div className="bubble-card p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-2">
        <input
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          className="border px-3 py-2 rounded"
          placeholder="Filter by player/team/date"
        />
        <select value={filterTeam} onChange={(event) => setFilterTeam(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">All teams</option>
          {teamOptions.map((team) => (
            <option key={team} value={team}>{team}</option>
          ))}
        </select>
        <input
          type="number"
          min={1}
          value={filterMinRules}
          onChange={(event) => setFilterMinRules(event.target.value)}
          className="border px-3 py-2 rounded"
          placeholder="Min rules violated"
        />
      </div>

      <div className="bubble-table-wrapper">
        <table className="bubble-table">
          <thead>
            <tr>
              <th />
              <th><button className="table-sort" onClick={() => toggleSort("date")}>Date</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("player")}>Player</button></th>
              <th>Team</th>
              <th><button className="table-sort" onClick={() => toggleSort("count")}>Rules Violated</button></th>
            </tr>
          </thead>
          <tbody>
            {sortedViolations.length === 0 && (
              <tr>
                <td colSpan={5} className="text-gray-600">No violations found.</td>
              </tr>
            )}
            {sortedViolations.map((violation) => {
              const expanded = Boolean(expandedRows[violation.id]);
              return (
                <React.Fragment key={violation.id}>
                  <tr>
                    <td>
                      <button
                        className="table-sort"
                        onClick={() => setExpandedRows((prev) => ({ ...prev, [violation.id]: !prev[violation.id] }))}
                      >
                        {expanded ? "Hide" : "Show"}
                      </button>
                    </td>
                    <td>{violation.date}</td>
                    <td>
                      <button className="table-sort" onClick={() => setActivePlayerModalId(violation.playerId)}>
                        {violation.playerName}
                      </button>
                    </td>
                    <td>{violation.teamName}</td>
                    <td>{violation.rules.length}</td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={5}>
                        <div className="p-2">
                          <table className="bubble-table">
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th>Rule</th>
                                <th>Source</th>
                                <th>Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {violation.rules.map((rule) => (
                                <tr key={rule.id}>
                                  <td>{rule.type}</td>
                                  <td>{rule.ruleName}</td>
                                  <td>{rule.source}</td>
                                  <td>{rule.details}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    {activePlayerModalId && (
      <PlayerProfileModal
        playerId={activePlayerModalId}
        userRoles={userRoles}
        onClose={() => setActivePlayerModalId(null)}
      />
    )}
    </>
  );
};

export default ViolationsPage;
